import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Download, Calendar, DollarSign, Users, TrendingUp, Eye, X, Receipt } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SharedHeader from './SharedHeader';
import { toast } from 'react-hot-toast';

const SalesHistory = () => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showSaleModal, setShowSaleModal] = useState(false);

  const businessConfig = {
    businessName: currentUser?.displayName || 'My Business',
    businessEmail: currentUser?.email || 'business@example.com',
    businessPhone: '+91 98765 43210',
    businessAddress: '123 Business Street, City, State 12345',
    fixedDiscount: 10,
    pointsPerRupee: 0.1,
    minRedemptionPoints: 100
  };  
  // Mock sales data with today's date for testing
  const today = new Date().toISOString().split('T')[0];
  const salesHistory = [
    {
      id: 1,
      customer: 'Rahul Sharma',
      email: 'rahul.sharma@email.com',
      amount: '₹1,250',
      items: '3 items',
      paymentMethod: 'UPI',
      date: today, // Today's transaction
      status: 'Completed',
      orderId: 'ORD-001',
      pointsEarned: 125
    },
    {
      id: 2,
      customer: 'Priya Patel',
      email: 'priya.patel@email.com',
      amount: '₹850',
      items: '2 items',
      paymentMethod: 'Card',
      date: today, // Today's transaction
      status: 'Processing',
      orderId: 'ORD-002',
      pointsEarned: 85
    },
    {
      id: 3,
      customer: 'Amit Kumar',
      email: 'amit.kumar@email.com',
      amount: '₹2,100',
      items: '5 items',
      paymentMethod: 'Cash',
      date: '2024-01-13',
      status: 'Completed',
      orderId: 'ORD-003',
      pointsEarned: 210
    },
    {
      id: 4,
      customer: 'Sneha Gupta',
      email: 'sneha.gupta@email.com',
      amount: '₹650',
      items: '1 item',
      paymentMethod: 'UPI',
      date: '2024-01-12',
      status: 'Refunded',
      orderId: 'ORD-004',
      pointsEarned: 0
    },
    {
      id: 5,
      customer: 'Vikash Singh',
      email: 'vikash.singh@email.com',
      amount: '₹1,800',
      items: '4 items',
      paymentMethod: 'Card',
      date: '2024-01-11',
      status: 'Completed',
      orderId: 'ORD-005',
      pointsEarned: 180
    }
  ];

  // Sorting function
  const sortData = (data, field, direction) => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle numeric values (amount and points)
      if (field === 'amount') {
        aValue = parseFloat(aValue.replace(/[₹,]/g, ''));
        bValue = parseFloat(bValue.replace(/[₹,]/g, ''));
      } else if (field === 'pointsEarned') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else if (field === 'date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter function for sales data
  const filterData = (data, searchTerm, filterType, dateRange, customFromDate, customToDate) => {
    let filtered = data;

    // Filter by search term (customer name or email)
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.orderId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterType !== 'all') {
      filtered = filtered.filter(item =>
        item.status.toLowerCase() === filterType.toLowerCase()
      );
    }

    // Filter by date range
    if (dateRange !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
        
        switch (dateRange) {
          case 'today':
            return itemDate.getTime() === today.getTime();
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return itemDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return itemDate >= monthAgo;
          case 'custom':
            if (customFromDate && customToDate) {
              const fromDate = new Date(customFromDate);
              const toDate = new Date(customToDate);
              fromDate.setHours(0, 0, 0, 0);
              toDate.setHours(23, 59, 59, 999);
              return itemDate >= fromDate && itemDate <= toDate;
            }
            return true;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const filteredSales = filterData(salesHistory, searchTerm, filterType, dateRange, customFromDate, customToDate);
  const sortedSales = sortData(filteredSales, sortField, sortDirection);

  // Calculate summary statistics
  const totalSales = filteredSales.reduce((sum, sale) => {
    const amount = parseFloat(sale.amount.replace('₹', '').replace(',', ''));
    return sum + amount;
  }, 0);

  const completedSales = filteredSales.filter(sale => sale.status === 'Completed').length;
  const totalCustomers = new Set(filteredSales.map(sale => sale.email)).size;

  const handleExport = () => {
    toast.success('Sales data exported successfully!');
  };

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setShowSaleModal(true);
  };

  const closeSaleModal = () => {
    setSelectedSale(null);
    setShowSaleModal(false);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Calculate pagination
  const totalPages = Math.ceil(sortedSales.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSales = sortedSales.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    navigate('/business/dashboard');
  };

  const handleMenuAction = (action) => {
    switch (action) {
      case 'manage-offers':
        navigate('/business/offers');
        break;
      case 'dashboard':
        navigate('/business/dashboard');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <SharedHeader 
                    title={businessConfig.businessName}
                    subtitle="Business Dashboard"
                    onMenuAction={handleMenuAction}
                  />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBack}
            className="text-indigo-600 hover:underline font-medium"
          >
            ← Back
          </button>
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Sales History</h1>
                <p className="text-gray-600">Complete transaction history and analytics</p>
              </div>
            </div>
            <button
              onClick={handleExport}
              className={`${theme.button} px-4 py-2 rounded-lg flex items-center space-x-2`}
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>

         
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by customer name, email, or order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
           
            <div className="lg:w-48">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {dateRange === 'custom' && (
            <div className="flex flex-col sm:flex-row gap-4 mt-4 pt-4 border-t border-gray-200">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <input
                  type="date"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <input
                  type="date"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Sales Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('orderId')}
                  >
                    Order ID {sortField === 'orderId' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer')}
                  >
                    Customer {sortField === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('email')}
                  >
                    Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="text-right py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}
                  >
                    Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-700">Items</th>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('paymentMethod')}
                  >
                    Payment {sortField === 'paymentMethod' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="text-right py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('pointsEarned')}
                  >
                    Points {sortField === 'pointsEarned' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('date')}
                  >
                    Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentSales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6 text-gray-900 font-medium">{sale.orderId}</td>
                    <td className="py-4 px-6 text-gray-900">{sale.customer}</td>
                    <td className="py-4 px-6 text-gray-700">{sale.email}</td>
                    <td className="py-4 px-6 text-gray-900 font-semibold">{sale.amount}</td>
                    <td className="py-4 px-6 text-gray-700">{sale.items}</td>
                    <td className="py-4 px-6 text-gray-700">{sale.paymentMethod}</td>
                    <td className="py-4 px-6 text-right text-blue-600 font-medium">{sale.pointsEarned}</td>
                    <td className="py-4 px-6 text-gray-500">{new Date(sale.date).toLocaleDateString()}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        sale.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                        sale.status === 'Processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {sale.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button 
                        onClick={() => handleViewSale(sale)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* No results message */}
          {currentSales.length === 0 && sortedSales.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sales records found</h3>
              <p className="text-gray-600">Try adjusting your search criteria or filters.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {sortedSales.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mt-6">
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedSales.length)} of {sortedSales.length} results
              </p>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded-lg ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sale Details Modal */}
        {showSaleModal && selectedSale && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Sale Details</h3>
                <button
                  onClick={closeSaleModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{selectedSale.customer}</h4>
                    <p className="text-sm text-gray-500">Order ID: {selectedSale.orderId}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="font-semibold text-lg">{selectedSale.amount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Points Earned</p>
                    <p className="font-semibold text-lg text-blue-600">{selectedSale.pointsEarned} pts</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Customer Email</span>
                      <span className="font-medium">{selectedSale.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Items</span>
                      <span className="font-medium">{selectedSale.items}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Method</span>
                      <span className="font-medium">{selectedSale.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date</span>
                      <span className="font-medium">{new Date(selectedSale.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Time</span>
                      <span className="font-medium">{new Date(selectedSale.date).toLocaleTimeString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        selectedSale.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                        selectedSale.status === 'Processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedSale.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesHistory;
